/**
 * Aiuto in linea del Portale Qualità (QA26-F5.2, F5.5) su @brandart/help-kit, con i due punti di aggancio della shell:
 *
 * - `AiutoStrato({ me, vista, children })`: provider del kit, presentazione al primo accesso per ruolo, suggerimenti sui
 *   `data-tip` della vista, pannello Guida. Va intorno al layout.
 * - `AiutoBarra({ compatto })`: pulsanti Assistente (solo se l'ambiente lo ha acceso, GET /auth/config) e Guida della
 *   testata, con `data-tip` «assistente» e «guida».
 *
 * Stato «già visto» sul server (GET/PUT /api/me/ui-prefs, chiavi onboarding.<ruolo>, tip.<anchor>, tip.disabled): se
 * il server non risponde non si mostra niente. La lingua è quella di i18next della shell (it | en).
 * KPI: gli eventi dell'aiuto vanno al beacon (solo etichette e numeri).
 */
import "@brandart/help-kit/styles.css";
import {
  createHelpStore,
  createOnboarding,
  GuidePanel,
  HelpKitProvider,
  HelpLayer,
  type GuideSection,
  type HelpEvent,
  type OnboardingSlide,
  type TipDef,
} from "@brandart/help-kit";
import { Button } from "antd";
import {
  AlertOutlined,
  ApartmentOutlined,
  AuditOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
  EuroOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  QuestionCircleOutlined,
  SmileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  ANCORA,
  MAX_SCHEDE,
  PRESENTAZIONE,
  RUOLI,
  profiliRf,
  profiloPrincipale,
  sezioniGuida,
  suggerimentiPer,
  SUGGERIMENTI,
  VERSIONE_PRESENTAZIONE,
  type Figura,
  type Lingua,
  type MeDto,
  type Ruolo,
  type Vista,
} from "@qa/shared";
import { AssistenteButton } from "./Assistente";
import { idScheda, tKit, UI } from "./testi";
import { traccia } from "./usage";

export const useHelpStore = createHelpStore<Ruolo>();

const SCHEDE = Object.fromEntries(
  RUOLI.map((r) => [r, PRESENTAZIONE[r].map((s, i): OnboardingSlide<Ruolo> => ({ id: idScheda(r, i), track: r, illustration: s.figura ?? "" }))]),
) as Record<Ruolo, OnboardingSlide<Ruolo>[]>;

/**
 * Ogni profilo Red Flag ha la sua presentazione: si vede quella del profilo principale dei propri ruoli (chi cambia
 * ruolo vede quella del ruolo nuovo). Chi ha solo ruoli NC per ora non ne ha (arriva con la sezione, NCI26-F3.6).
 */
export const onboarding = createOnboarding<Ruolo, MeDto>({
  version: String(VERSIONE_PRESENTAZIONE),
  maxSlides: MAX_SCHEDE,
  slides: SCHEDE,
  tracksFor: (me) => {
    const p = profiloPrincipale(me.ruoli);
    return p ? [p] : [];
  },
});

export const SUGGERIMENTI_KIT: TipDef<Vista, MeDto>[] = SUGGERIMENTI.map((s) => ({
  id: s.anchor,
  anchor: s.anchor,
  view: s.vista,
  audience: (me) => suggerimentiPer(me.ruoli).includes(s),
  steps: 0,
}));

/** KPI dell'aiuto: stessi nomi del contratto, inoltrati al beacon. */
export function suEventoAiuto(e: HelpEvent) {
  if (e.type === "guide_opened") traccia("guide_opened");
  else if (e.type === "onboarding_completed") traccia("onboarding_completed", { version: Math.max(1, e.version), skipped: e.skipped });
  else if (e.type === "tip_dismissed") traccia("tip_dismissed", { anchor: e.anchor });
  else if (e.type === "assistant_opened") traccia("assistant_opened", { source: e.source });
}

const FIGURE: Record<Figura, ReactNode> = {
  benvenuto: <SmileOutlined />,
  percorso: <NodeIndexOutlined />,
  ruolo: <UserOutlined />,
  segnala: <AlertOutlined />,
  nc: <AuditOutlined />,
  claim: <EuroOutlined />,
  report: <BarChartOutlined />,
  proposte: <CheckSquareOutlined />,
  assistente: <MessageOutlined />,
  guida: <QuestionCircleOutlined />,
};
function Figura({ slide }: { slide: OnboardingSlide<Ruolo> }) {
  const f = FIGURE[slide.illustration as Figura] ?? <ApartmentOutlined />;
  return <div className="aiuto-figura">{f}</div>;
}

/** La lingua dell'interfaccia (i18next della shell); italiano se non è inizializzato. */
export function useLingua(): Lingua {
  const { i18n } = useTranslation();
  return i18n?.language?.startsWith("en") ? "en" : "it";
}

export const PDF_MANUALE: Record<Lingua, string> = { it: "/manuale-qa.pdf", en: "/manuale-qa-en.pdf" };
const IL_TUO_RUOLO: Record<Lingua, string> = { it: "il tuo ruolo", en: "your role" };

/** Le sezioni del manuale nel formato del kit: le sezioni dei propri profili e la panoramica in cima e aperte. */
export const sezioniKit = (profili: readonly Ruolo[], lingua: Lingua): GuideSection[] =>
  sezioniGuida(profili, lingua).map((s) => ({ id: s.id, title: s.mia && s.id !== "panoramica" ? `${s.titolo} · ${IL_TUO_RUOLO[lingua]}` : s.titolo, source: s.testo, relevant: s.mia }));

type GuidaCtx = { apri: () => void };
const Ctx = createContext<GuidaCtx>({ apri: () => {} });
export const useGuida = () => useContext(Ctx);

/** Strato dell'aiuto intorno al layout. `vista` null = nessun suggerimento (login, pagine senza vista). */
export function AiutoStrato({ me, vista, children }: { me: MeDto; vista: Vista | null; children: ReactNode }) {
  const lingua = useLingua();
  const [guida, setGuida] = useState(false);
  const chiudi = useCallback(() => setGuida(false), []);
  const ctx = useMemo<GuidaCtx>(() => ({ apri: () => setGuida(true) }), []); // guide_opened lo manda il GuidePanel
  const profili = useMemo(() => profiliRf(me.ruoli), [me.ruoli]);
  const sezioni = useMemo(() => sezioniKit(profili, lingua), [profili, lingua]);
  return (
    <HelpKitProvider t={tKit(lingua)} onEvent={suEventoAiuto}>
      <Ctx.Provider value={ctx}>
        {children}
        <HelpLayer
          store={useHelpStore}
          onboarding={onboarding}
          tips={SUGGERIMENTI_KIT}
          me={me}
          view={(vista ?? "nessuna") as Vista}
          renderIllustration={(s) => <Figura slide={s} />}
          mainRegionId="main"
          // Tre ancore (cerca, assistente, guida) stanno NELLA testata: con l'altezza di default (48 px) help-kit le
          // considera coperte dalla barra e il loro suggerimento non comparirebbe mai (verifica nel browser, 02/11).
          topBarHeight={0}
        />
        {guida && <GuidePanel sections={sezioni} pdfUrl={PDF_MANUALE[lingua]} helpStore={useHelpStore} onClose={chiudi} />}
      </Ctx.Provider>
    </HelpKitProvider>
  );
}

/** Pulsanti della testata: Assistente (se acceso) e Guida. */
export function AiutoBarra({ compatto }: { compatto: boolean }) {
  const lingua = useLingua();
  const { apri } = useGuida();
  return (
    <>
      <AssistenteButton compatto={compatto} lingua={lingua} />
      <Button className="guide-btn" type="text" icon={<QuestionCircleOutlined />} onClick={apri} data-tip={ANCORA.guida} aria-label={UI[lingua].guida}>
        {compatto ? null : UI[lingua].guida}
      </Button>
    </>
  );
}
